import * as Sentry from "@sentry/react"
import { useAppDispatch, useAppSelector } from "app/hooks"
import { loginFromApp } from "features/auth/authSlice"
import { setFoodInFitVersion } from "features/notification/notificationSlice"
import { useEffect, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import PATHS from "router/paths"
import { useFailed } from "features/notification/hooks"
import { isAndroidWebView, isIOSWebView } from "utils/webViewDetection"

export const REDIRECT_URL_PARAM = "redirectUrl"
const FOOD_IN_FIT_VERSION = "foodInFitVersion"
const STUCK_LOGIN_TIMEOUT_MS = 10_000

export const useLoginNativeApp = () => {
  const navigate = useNavigate()
  const loginFromAppLoading = useAppSelector(
    (state) => state.auth.loginFromAppLoading,
  )

  const loginFromAppFailed = useAppSelector(
    (state) => state.auth.loginFromAppFailed,
  )

  const token = useAppSelector((state) => state.auth.token)
  const [params] = useSearchParams()
  const redirectUrl = params.get(REDIRECT_URL_PARAM) || PATHS.app.root
  const foodInFitVersion = params.get(FOOD_IN_FIT_VERSION) || "v1"
  const validFoodVersion =
    foodInFitVersion === "v1" || foodInFitVersion === "v2"

  const wasHandlerCalled = useRef(false)
  const wasLoginSuccessful = useRef(false)

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (validFoodVersion) {
      dispatch(setFoodInFitVersion(foodInFitVersion))
    }
  }, [validFoodVersion])

  useEffect(() => {
    if (token) {
      wasLoginSuccessful.current = true
      navigate(redirectUrl)
    }
  }, [token])

  useEffect(() => {
    const isWebView = isIOSWebView() || isAndroidWebView()
    if (!isWebView) return

    const timer = setTimeout(() => {
      if (wasHandlerCalled.current || wasLoginSuccessful.current) return

      Sentry.withScope((scope) => {
        scope.setFingerprint(["LoginInNativeApp", "stuck-no-callback"])
        scope.setContext("stuck_login_context", {
          platform: isIOSWebView() ? "ios" : "android",
          redirectUrl,
          foodInFitVersion,
          timeoutMs: STUCK_LOGIN_TIMEOUT_MS,
        })
        Sentry.captureMessage(
          "[LoginInNativeApp] Stuck: native app did not call login handler",
          "warning",
        )
      })
    }, STUCK_LOGIN_TIMEOUT_MS)

    return () => clearTimeout(timer)
  }, [])

  useFailed(loginFromAppFailed)

  const handleLoginInNativeApp = (refreshToken: string) => {
    wasHandlerCalled.current = true
    dispatch(loginFromApp(refreshToken))
  }

  return {
    loginFromAppLoading,
    loginFromAppFailed,
    handleLoginInNativeApp,
  }
}
